import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Eye, Edit2, Ban, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountsTab = ({ token }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  // Filter states
  const [accountType, setAccountType] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Phase 0/1 — admin auth via HttpOnly session cookie; omit Bearer header
  // when token is null so the backend's cookie path is used instead.
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAccounts();
  }, [accountType, status, page]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (accountType !== 'all') params.append('type', accountType);
      if (status !== 'all') params.append('status', status);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', 20);

      const response = await axios.get(`${API}/admin/accounts?${params}`, { headers });
      setAccounts(response.data.accounts || []);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (accountId) => {
    try {
      const response = await axios.get(`${API}/admin/accounts/${accountId}`, { headers });
      setCurrentAccount(response.data);
      setEditData({
        full_name: response.data.user.full_name,
        phone_number: response.data.user.phone_number,
        business_name: response.data.user.business_name,
        province: response.data.user.province,
        business_address: response.data.user.business_address,
      });
      setSheetOpen(true);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to fetch account details');
    }
  };

  const handleStatusChange = async (accountId, newStatus) => {
    try {
      await axios.put(`${API}/admin/accounts/${accountId}/status`, {
        status: newStatus,
        reason: 'Admin action'
      }, { headers });
      toast.success(`Account ${newStatus}`);
      fetchAccounts();
      setSheetOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account status');
    }
  };

  const handleSaveEdit = async () => {
    if (!currentAccount) return;
    try {
      await axios.put(`${API}/admin/accounts/${currentAccount.user.id}`, editData, { headers });
      toast.success('Account updated');
      setEditMode(false);
      handleViewDetails(currentAccount.user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchAccounts();
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter Accounts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Account Type</Label>
              <Select value={accountType} onValueChange={setAccountType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="merchant">Merchants</SelectItem>
                  <SelectItem value="customer">Customers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Search</Label>
              <Input
                placeholder="Email, name, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]">
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
          <CardDescription>Total: {accounts.length} results</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="animate-spin" size={24} />
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader className="bg-[#F5EDE4]">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Province</TableHead>
                    <TableHead>Wallet Balance</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{account.full_name}</TableCell>
                      <TableCell className="text-sm">{account.email}</TableCell>
                      <TableCell>
                        <Badge variant={account.user_type === 'merchant' ? 'default' : 'secondary'}>
                          {account.user_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{account.phone_number}</TableCell>
                      <TableCell>
                        <Badge variant={account.status === 'active' ? 'default' : 'destructive'}>
                          {account.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{account.province || 'N/A'}</TableCell>
                      <TableCell className="text-right font-semibold">
                        R{account.wallet_balance?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(account.id)}
                        >
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[500px]">
          <SheetHeader>
            <SheetTitle>Account Details</SheetTitle>
            <SheetDescription>
              {currentAccount?.user.email}
            </SheetDescription>
          </SheetHeader>

          {currentAccount && (
            <div className="space-y-6 mt-6">
              {/* User Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3">User Information</h3>
                <div className="space-y-3">
                  {editMode ? (
                    <>
                      <div>
                        <Label>Full Name</Label>
                        <Input
                          value={editData.full_name || ''}
                          onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Phone Number</Label>
                        <Input
                          value={editData.phone_number || ''}
                          onChange={(e) => setEditData({...editData, phone_number: e.target.value})}
                        />
                      </div>
                      {currentAccount.user.user_type === 'merchant' && (
                        <>
                          <div>
                            <Label>Business Name</Label>
                            <Input
                              value={editData.business_name || ''}
                              onChange={(e) => setEditData({...editData, business_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Province</Label>
                            <Input
                              value={editData.province || ''}
                              onChange={(e) => setEditData({...editData, province: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Business Address</Label>
                            <Input
                              value={editData.business_address || ''}
                              onChange={(e) => setEditData({...editData, business_address: e.target.value})}
                            />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{currentAccount.user.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Full Name:</span>
                        <span className="font-medium">{currentAccount.user.full_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{currentAccount.user.phone_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type:</span>
                        <Badge>{currentAccount.user.user_type}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <Badge variant={currentAccount.user.status === 'active' ? 'default' : 'destructive'}>
                          {currentAccount.user.status}
                        </Badge>
                      </div>
                      {currentAccount.user.user_type === 'merchant' && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Business:</span>
                            <span className="font-medium">{currentAccount.user.business_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Province:</span>
                            <span className="font-medium">{currentAccount.user.province}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Wallet Info */}
              {currentAccount.wallet && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Wallet</h3>
                  <div className="space-y-2 bg-gray-50 p-3 rounded">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Wallet ID:</span>
                      <span className="font-mono text-sm">{currentAccount.wallet.wallet_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Balance:</span>
                      <span className="font-bold text-lg">R{currentAccount.wallet.balance?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ACE Tokens:</span>
                      <span className="font-medium">{currentAccount.wallet.ace_token_balance?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Transactions */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Recent Transactions</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {currentAccount.recent_transactions?.map((tx, idx) => (
                    <div key={idx} className="border rounded p-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{tx.type}</span>
                        <span className="font-bold text-green-600">R{tx.amount?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>{new Date(tx.created_at).toLocaleString()}</span>
                        <Badge variant="outline" className="text-xs">{tx.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <Button onClick={handleSaveEdit} className="flex-1 bg-green-600 hover:bg-green-700">
                      <Check size={16} className="mr-2" /> Save Changes
                    </Button>
                    <Button onClick={() => setEditMode(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => setEditMode(true)} variant="outline" className="flex-1">
                      <Edit2 size={16} className="mr-2" /> Edit Info
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="flex-1">
                          <Ban size={16} className="mr-2" />
                          {currentAccount.user.status === 'active' ? 'Suspend' : 'Activate'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {currentAccount.user.status === 'active' ? 'Suspend Account' : 'Activate Account'}?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This action {currentAccount.user.status === 'active' ? 'will suspend' : 'will activate'} the account.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogAction
                          onClick={() => handleStatusChange(currentAccount.user.id, currentAccount.user.status === 'active' ? 'suspended' : 'active')}
                        >
                          Confirm
                        </AlertDialogAction>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
